// @ts-nocheck
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  ArrowLeft,
  CheckCircle,
  CloudOff,
  Loader2,
  Play,
  RefreshCw,
  RotateCcw,
} from 'lucide-react';
import { useResilientSave } from '@/hooks/useResilientSave';
import { getDivisionColor } from '@/utils/divisionColors';

const NAVY = '#0a1f44';
const MAX_SCORE = 199;

const PERIOD_META: Record<string, { label: string; abbr: string; defaultCount: number }> = {
  quarters: { label: 'Quarter', abbr: 'Q', defaultCount: 4 },
  halves: { label: 'Half', abbr: 'H', defaultCount: 2 },
  innings: { label: 'Inning', abbr: '', defaultCount: 9 },
  periods: { label: 'Period', abbr: 'P', defaultCount: 3 },
};

function periodLabel(type: string, n: number) {
  const meta = PERIOD_META[type];
  if (!meta) return String(n);
  if (type === 'innings') return String(n);
  return `${meta.abbr}${n}`;
}

function incrementsForSport(sport?: string): number[] {
  const s = (sport || '').toLowerCase();
  if (s.includes('volley')) return [1];
  if (s.includes('soccer') || s.includes('hockey') || s.includes('lacrosse')) return [1];
  if (s.includes('base') || s.includes('soft')) return [1, 2, 3, 4];
  if (s.includes('foot')) return [1, 2, 3, 6, 7];
  return [1, 2, 3]; // basketball default
}

const clamp = (n: number) => Math.max(0, Math.min(MAX_SCORE, n));
const clampStr = (raw: string) => {
  const cleaned = raw.replace(/[^0-9]/g, '').slice(0, 3);
  if (cleaned === '') return '';
  return String(clamp(parseInt(cleaned, 10)));
};

/** Big, arm's-length score column for one team. */
const TeamScoreColumn: React.FC<{
  name: string;
  value: string;
  increments: number[];
  disabled: boolean;
  onSetValue: (v: string) => void;
  onBump: (delta: number) => void;
}> = ({ name, value, increments, disabled, onSetValue, onBump }) => (
  <div className="flex min-w-0 flex-1 flex-col items-center gap-3">
    <p className="line-clamp-2 min-h-[2.5rem] text-center text-base font-bold leading-tight text-foreground">
      {name}
    </p>
    <Input
      type="text"
      inputMode="numeric"
      pattern="[0-9]*"
      maxLength={3}
      placeholder="0"
      aria-label={`${name} score`}
      disabled={disabled}
      value={value}
      onChange={(e) => onSetValue(clampStr(e.target.value))}
      onFocus={(e) => e.currentTarget.select()}
      className="h-24 w-full rounded-xl text-center text-6xl font-extrabold tabular-nums"
      style={{ color: NAVY }}
    />
    <div className="grid w-full grid-cols-3 gap-2">
      {increments.map((inc) => (
        <Button
          key={inc}
          type="button"
          variant="outline"
          disabled={disabled}
          onClick={() => onBump(inc)}
          className="h-14 text-lg font-bold"
        >
          +{inc}
        </Button>
      ))}
      <Button
        type="button"
        variant="ghost"
        disabled={disabled}
        onClick={() => onBump(-1)}
        className="h-14 text-lg font-bold text-muted-foreground"
      >
        −1
      </Button>
    </div>
  </div>
);

const GameScoringSheet: React.FC<{
  game: any;
  canScore: boolean;
  periodConfig?: { period_type: string; period_count: number };
  sport?: string;
  callScore: (gameId: string, payload: any) => Promise<any>;
  onGameUpdated: (g: any) => void;
  onClose: () => void;
}> = ({ game, canScore, periodConfig, sport, callScore, onGameUpdated, onClose }) => {
  const periodType = periodConfig?.period_type || 'none';
  const periodsOn = periodType !== 'none';
  const periodCount = periodConfig?.period_count || PERIOD_META[periodType]?.defaultCount || 4;
  const increments = useMemo(() => incrementsForSport(sport), [sport]);
  const c = getDivisionColor(game.division);

  const [aVal, setAVal] = useState(String(game.score_a ?? 0));
  const [bVal, setBVal] = useState(String(game.score_b ?? 0));
  const [busy, setBusy] = useState(false);
  const [confirmComplete, setConfirmComplete] = useState(false);
  const dirtyRef = useRef(false);

  const status = game.status || 'scheduled';
  const completed = status === 'completed';

  // Resilient live-score saver: absolute totals, latest-wins, auto-retry.
  const saver = useResilientSave<{ a: number; b: number }>(async ({ a, b }) => {
    const res = await callScore(game.id, { action: 'update_score', score_a: a, score_b: b });
    onGameUpdated({
      id: game.id,
      score_a: res?.game?.score_a ?? a,
      score_b: res?.game?.score_b ?? b,
      status: res?.game?.game_status ?? 'in_progress',
    });
  });

  // Adopt server values only when the scorer has nothing unsaved in flight.
  useEffect(() => {
    if (dirtyRef.current || saver.status === 'saving' || saver.status === 'unsaved') return;
    setAVal(String(game.score_a ?? 0));
    setBVal(String(game.score_b ?? 0));
  }, [game.score_a, game.score_b, saver.status]);

  const numA = parseInt(aVal || '0', 10) || 0;
  const numB = parseInt(bVal || '0', 10) || 0;

  // The latest totals, read at click time. Reading numA/numB from the render
  // closure let two quick clicks both start from the same stale number.
  const curRef = useRef({ a: numA, b: numB });
  curRef.current = { a: numA, b: numB };

  const pushScore = (a: number, b: number) => {
    dirtyRef.current = true;
    curRef.current = { a, b };
    setAVal(String(a));
    setBVal(String(b));
    if (canScore && !periodsOn) saver.save({ a, b });
  };

  const bumpA = (d: number) => pushScore(clamp(curRef.current.a + d), curRef.current.b);
  const bumpB = (d: number) => pushScore(curRef.current.a, clamp(curRef.current.b + d));

  const typeA = (v: string) => {
    dirtyRef.current = true;
    setAVal(v);
    if (canScore && !periodsOn) saver.save({ a: parseInt(v || '0', 10) || 0, b: numB });
  };
  const typeB = (v: string) => {
    dirtyRef.current = true;
    setBVal(v);
    if (canScore && !periodsOn) saver.save({ a: numA, b: parseInt(v || '0', 10) || 0 });
  };

  const run = async (payload: any) => {
    setBusy(true);
    try {
      const res = await callScore(game.id, payload);
      const g = res?.game;
      onGameUpdated({
        id: game.id,
        score_a: g?.score_a ?? game.score_a,
        score_b: g?.score_b ?? game.score_b,
        status: g?.game_status ?? game.status,
        periods: res?.cumulative ?? res?.periods ?? game.periods,
      });
      dirtyRef.current = false;
      return res;
    } finally {
      setBusy(false);
    }
  };

  const winnerName =
    numA > numB ? game.team_a : numB > numA ? game.team_b : null;

  // ---- period entry state (CUMULATIVE running scoreboard totals) ----
  const savedPeriods = useMemo(() => {
    const map: Record<number, any> = {};
    (game.periods || []).forEach((p: any) => (map[p.period_number] = p));
    return map;
  }, [game.periods]);
  const [pVals, setPVals] = useState<Record<number, { a: string; b: string }>>({});
  useEffect(() => {
    const init: Record<number, { a: string; b: string }> = {};
    (game.periods || []).forEach((p: any) => {
      init[p.period_number] = {
        a: p.cum_a == null ? '' : String(p.cum_a),
        b: p.cum_b == null ? '' : String(p.cum_b),
      };
    });
    setPVals(init);
  }, [game.periods]);

  const periodSaver = useResilientSave<{ n: number; a: number; b: number }>(async ({ n, a, b }) => {
    const res = await callScore(game.id, {
      action: 'set_period',
      period_number: n,
      cum_a: a,
      cum_b: b,
    });
    onGameUpdated({
      id: game.id,
      score_a: res?.total?.score_a ?? res?.game?.score_a ?? game.score_a,
      score_b: res?.total?.score_b ?? res?.game?.score_b ?? game.score_b,
      status: res?.game?.game_status ?? 'in_progress',
      periods: res?.cumulative ?? res?.periods ?? game.periods,
    });
  });

  const unsaved = saver.status === 'unsaved' || periodSaver.status === 'unsaved';
  const savingNow = saver.status === 'saving' || periodSaver.status === 'saving';

  return (
    <div className="fixed inset-0 z-50 flex flex-col overflow-y-auto bg-background">
      {/* Header */}
      <div
        className="sticky top-0 z-10 flex items-center gap-2 border-b bg-card px-3 py-3"
        style={{ borderLeft: `6px solid ${c.border}` }}
      >
        <Button variant="ghost" size="sm" className="h-11 px-2" onClick={onClose}>
          <ArrowLeft className="h-5 w-5" />
          <span className="ml-1">Games</span>
        </Button>
        <div className="min-w-0 flex-1 text-right">
          <p className="truncate text-sm font-semibold" style={{ color: NAVY }}>
            {game.court || 'Court TBD'}
            {game.start ? ` · ${game.start}` : ''}
          </p>
          <p className="truncate text-xs text-muted-foreground">
            {game.division || ''} · {completed ? 'Final' : status === 'in_progress' ? 'In progress' : 'Not started'}
          </p>
        </div>
      </div>

      {/* Save state banner */}
      {(unsaved || savingNow) && (
        <div
          className={`flex items-center gap-2 px-4 py-2 text-sm ${
            unsaved ? 'bg-destructive/10 text-destructive' : 'bg-muted text-muted-foreground'
          }`}
        >
          {unsaved ? <CloudOff className="h-4 w-4" /> : <Loader2 className="h-4 w-4 animate-spin" />}
          <span className="flex-1">
            {unsaved
              ? 'Not saved — your score is safe on screen. Retrying automatically.'
              : 'Saving…'}
          </span>
          {unsaved && (
            <Button
              size="sm"
              variant="outline"
              className="h-9"
              onClick={() => {
                saver.retry();
                periodSaver.retry();
              }}
            >
              <RefreshCw className="mr-1 h-4 w-4" />
              Retry
            </Button>
          )}
        </div>
      )}

      <div className="mx-auto w-full max-w-xl flex-1 px-4 py-5">
        {periodsOn ? (
          <>
            <div className="mb-4 flex items-end justify-center gap-4 tabular-nums" style={{ color: NAVY }}>
              <div className="min-w-0 flex-1 text-center">
                <p className="line-clamp-2 text-sm font-bold">{game.team_a}</p>
                <p className="text-5xl font-extrabold">{game.score_a ?? 0}</p>
              </div>
              <span className="pb-3 text-2xl text-muted-foreground">–</span>
              <div className="min-w-0 flex-1 text-center">
                <p className="line-clamp-2 text-sm font-bold">{game.team_b}</p>
                <p className="text-5xl font-extrabold">{game.score_b ?? 0}</p>
              </div>
            </div>

            <div className="rounded-xl border bg-muted/30 p-3">
              <p className="mb-1 text-sm font-bold" style={{ color: NAVY }}>
                Running scoreboard total (cumulative)
              </p>
              <p className="mb-3 text-xs text-muted-foreground">
                Enter the total score shown on the scoreboard at the END of each{' '}
                {PERIOD_META[periodType]?.label?.toLowerCase() || 'period'} — not the points
                scored in it. Per-{PERIOD_META[periodType]?.label?.toLowerCase() || 'period'} points
                are worked out for you.
              </p>
              <div className="space-y-3">
                {Array.from({ length: periodCount }, (_, i) => i + 1).map((n) => {
                  const savedP = savedPeriods[n];
                  const commit = () => {
                    const a = clamp(parseInt(pVals[n]?.a || '0', 10) || 0);
                    const b = clamp(parseInt(pVals[n]?.b || '0', 10) || 0);
                    if (!canScore) return;
                    periodSaver.save({ n, a, b });
                  };
                  return (
                    <div key={n} className="flex items-center gap-2">
                      <span
                        className="w-10 shrink-0 text-sm font-bold"
                        style={{ color: savedP ? NAVY : '#9ca3af' }}
                      >
                        {periodLabel(periodType, n)}
                      </span>
                      <Input
                        type="text"
                        inputMode="numeric"
                        aria-label={`${game.team_a} cumulative total after ${periodLabel(periodType, n)}`}
                        disabled={!canScore || busy}
                        value={pVals[n]?.a ?? ''}
                        onChange={(e) =>
                          setPVals((p) => ({
                            ...p,
                            [n]: { a: clampStr(e.target.value), b: p[n]?.b ?? '' },
                          }))
                        }
                        onFocus={(e) => e.currentTarget.select()}
                        onBlur={commit}
                        className="h-14 flex-1 text-center text-2xl font-extrabold tabular-nums"
                        style={{ color: NAVY }}
                      />
                      <Input
                        type="text"
                        inputMode="numeric"
                        aria-label={`${game.team_b} cumulative total after ${periodLabel(periodType, n)}`}
                        disabled={!canScore || busy}
                        value={pVals[n]?.b ?? ''}
                        onChange={(e) =>
                          setPVals((p) => ({
                            ...p,
                            [n]: { a: p[n]?.a ?? '', b: clampStr(e.target.value) },
                          }))
                        }
                        onFocus={(e) => e.currentTarget.select()}
                        onBlur={commit}
                        className="h-14 flex-1 text-center text-2xl font-extrabold tabular-nums"
                        style={{ color: NAVY }}
                      />
                      <span className="w-20 shrink-0 text-right text-xs font-semibold tabular-nums text-muted-foreground">
                        {savedP && savedP.pts_a != null ? `+${savedP.pts_a} / +${savedP.pts_b}` : '–'}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        ) : (
          <div className="flex items-start gap-4">
            <TeamScoreColumn
              name={game.team_a}
              value={aVal}
              increments={increments}
              disabled={!canScore || completed || busy}
              onSetValue={typeA}
              onBump={bumpA}
            />
            <span className="pt-14 text-3xl text-muted-foreground">–</span>
            <TeamScoreColumn
              name={game.team_b}
              value={bVal}
              increments={increments}
              disabled={!canScore || completed || busy}
              onSetValue={typeB}
              onBump={bumpB}
            />
          </div>
        )}

        {/* Actions */}
        {canScore && (
          <div className="mt-8 space-y-3 pb-10">
            {status === 'scheduled' && (
              <Button
                className="h-16 w-full text-lg font-bold"
                disabled={busy}
                onClick={() => run({ action: 'start' })}
              >
                <Play className="mr-2 h-5 w-5" />
                Start game
              </Button>
            )}

            {!completed && (
              <Button
                className="h-16 w-full text-lg font-bold"
                variant="default"
                disabled={busy}
                onClick={() => setConfirmComplete(true)}
              >
                <CheckCircle className="mr-2 h-5 w-5" />
                {periodsOn ? 'Finalize game' : 'Complete game'}
              </Button>
            )}

            {completed && (
              <Button
                className="h-16 w-full text-lg font-bold"
                variant="outline"
                disabled={busy}
                onClick={() => run({ action: 'reopen' })}
              >
                <RotateCcw className="mr-2 h-5 w-5" />
                Reopen for correction
              </Button>
            )}
          </div>
        )}
      </div>

      <AlertDialog open={confirmComplete} onOpenChange={setConfirmComplete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Finish this game?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p className="text-lg font-bold text-foreground">
                  {game.team_a} {periodsOn ? (game.score_a ?? 0) : numA} — {periodsOn ? (game.score_b ?? 0) : numB}{' '}
                  {game.team_b}
                </p>
                <p>
                  {periodsOn
                    ? 'Totals come from the periods entered above.'
                    : winnerName
                      ? `${winnerName} wins.`
                      : 'This game is a tie — no winner will be recorded.'}
                </p>
                <p>This publishes to the standings. You can reopen it afterwards if it is wrong.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="h-12">Not yet</AlertDialogCancel>
            <AlertDialogAction
              className="h-12"
              onClick={async () => {
                setConfirmComplete(false);
                if (periodsOn) await run({ action: 'finalize_periods' });
                else await run({ action: 'complete', score_a: numA, score_b: numB });
              }}
            >
              Yes, finish
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default GameScoringSheet;
